import { ref, onMounted, onUnmounted } from 'vue';
import { App } from '@capacitor/app';
import { useCapacitor } from './useCapacitor';

/**
 * Composable for network status detection
 */
export function useNetworkStatus() {
  const { isNative } = useCapacitor();
  const isOnline = ref(navigator.onLine);
  const wasOffline = ref(false);

  /**
   * Update network status
   */
  function updateStatus(online: boolean) {
    isOnline.value = online;
    if (!online) {
      wasOffline.value = true;
    }
  }

  /**
   * Setup network listeners
   */
  function setupListeners() {
    // Browser/Web listeners
    const handleOnline = () => updateStatus(true);
    const handleOffline = () => updateStatus(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Native app state change (when app resumes from background)
    if (isNative) {
      App.addListener('appStateChange', (state) => {
        // When app becomes active, check network status
        if (state.isActive) {
          updateStatus(navigator.onLine);
        }
      });

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        // Listener cleanup handled by Capacitor
      };
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }

  /**
   * Initialize network status monitoring
   */
  function initialize() {
    updateStatus(navigator.onLine);
    return setupListeners();
  }

  return {
    isOnline,
    wasOffline,
    initialize
  };
}
